import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getOneProduct, getProductsByPage, syncWithERP } from 'src/api/server/product';

// ----------------------------------------------------------------------

interface FetchProductsParams {
  idUser: string;
  config: string;
  page: number;
  replaceProducts: boolean;
}

interface FetchOneProductParams {
  idUser: string;
  config: string;
  id: string;
}

interface ProductPayload {
  replaceProducts: boolean;
  products: any[];
  nextPage?: number;
  fieldsSearchable: string[];
}

export const fetchProductsData = createAsyncThunk<ProductPayload, FetchProductsParams>(
  'products/fetchProductData',
  async ({ idUser, config, page, replaceProducts }) => {
    const products = []
    let nextPage = page;
    let fieldsSearchable = ['barcode', 'sku', 'name'];
    do {
      const result = await getProductsByPage(idUser, config, nextPage);
      products.push(...result.data);
      nextPage = result.pagination?.nextPage;
      if (result.fieldsSearchable?.[0]) fieldsSearchable = result.fieldsSearchable;
    } while (nextPage);
    syncWithERP(idUser, config).catch((error) => console.error('Sync with ERP failed:', error));
    return {
      nextPage,
      replaceProducts,
      products,
      fieldsSearchable
    };
  }
);

export const fetchOneProduct = createAsyncThunk<any, FetchOneProductParams>(
  'products/fetchOneProduct',
  async ({ idUser, config, id }) => {
    const product = await getOneProduct(idUser, config, id);
    console.info('fetchOneProduct product', product);
    return product;
  }
);

interface ProductsState {
  products: any[];
  isLoading: boolean;
  error: string | undefined;
  isValidating: boolean;
  lastUpdatedDate: string | undefined;
  nextPage: number;
  fieldsSearchable: string[];
}

const initialState: ProductsState = {
  products: [],
  isLoading: false,
  error: undefined,
  isValidating: false,
  lastUpdatedDate: undefined,
  nextPage: 1,
  fieldsSearchable: [],
};

const slice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    resetProducts: (state) => {
      Object.entries(initialState).forEach(([key, value]) => {
        (state as any)[key] = value;
      });
    },
    updateProducts: (state, action) => {
      const { data, fieldsSearchable } = action.payload;
      const now = new Date().toISOString();

      data.forEach((updatedProduct: any) => {
        const existingIndex = state.products.findIndex(
          (p) =>
            p.id === updatedProduct.id ||
            p.sku === updatedProduct.sku ||
            p.idERP === updatedProduct.id
        );
        const productToUpdate = {
          ...updatedProduct,
          lastUpdatedDate: now,
          images: Array.isArray(updatedProduct.images) ? [...updatedProduct.images] : [],
          colors: Array.isArray(updatedProduct.colors) ? [...updatedProduct.colors] : [],
          sizes: Array.isArray(updatedProduct.sizes) ? [...updatedProduct.sizes] : [],
        };

        productToUpdate.coverUrl = productToUpdate.images[0]?.url ?? productToUpdate.images[0];

        if (existingIndex === -1) {
          state.products.push({
            ...productToUpdate,
            importedAt: now,
          });
        } else {
          const productFound = state.products[existingIndex];
          productToUpdate.id = productFound.id;
          state.products[existingIndex] = {
            ...state.products[existingIndex],
            ...productToUpdate,
            importedAt: state.products[existingIndex].importedAt,
          };
        }

        state.isLoading = false;
        state.isValidating = true;
        state.error = undefined;
      });

      if (fieldsSearchable?.[0]) state.fieldsSearchable = fieldsSearchable;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProductsData.pending, (state) => {
        if (!state.products[0]) {
          state.isLoading = true;
          state.error = undefined;
          state.isValidating = false;
        }
      })
      .addCase(fetchProductsData.fulfilled, (state, action) => {
        const { products, nextPage, fieldsSearchable, replaceProducts } = action.payload;
        const now = new Date().toISOString();

        state.isLoading = false;
        state.isValidating = true;
        state.error = undefined;
        state.nextPage = nextPage ?? state.nextPage;
        state.fieldsSearchable = fieldsSearchable;

        const updatedProducts = products.map((product) => {
          const existingProduct = state.products.find(
            (p) => p?.id === product.id || p?.id === product.idERP || p?.sku === product.sku
          );

          const productToUpdate = {
            id: existingProduct?.id || product.id,
            sku: existingProduct?.sku || product.sku,
            name: product.name,
            price: product.price,
            images: Array.isArray(product.images)
              ? [...product.images.map((img: any) => img.url ?? img)]
              : [],
            category: product.category,
            quantity: product.quantity,
            available: product.available,
            coverUrl: product.images?.[0]?.url ?? product.images?.[0],
            colors: [],
            sizes: [],
            salesLastWeek: product.salesLastWeek,
            salesLastMonth: product.salesLastMonth,
            averagePerWeek: product.averagePerWeek,
            lastUpdatedDate: now,
          };

          fieldsSearchable.forEach((field: string) => {
            if (product[field] !== undefined) {
              (productToUpdate as any)[field] = product[field];
            }
          });

          return {
            ...(existingProduct || {}),
            ...productToUpdate,
            importedAt: existingProduct?.importedAt || now,
          };
        });

        const existingProducts = state.products.filter(
          (p) => !products.some((np) => np.id === p.id)
        );

        state.products = replaceProducts
          ? updatedProducts
          : [...existingProducts, ...updatedProducts];
      })
      .addCase(fetchProductsData.rejected, (state, action) => {
        state.isLoading = false;
        state.isValidating = false;
        state.error = action.error?.message;
      })
      .addCase(fetchOneProduct.fulfilled, (state, action) => {
        const product = action.payload;
        const existingIndex = state.products.findIndex(
          (p) => p?.id === product.id || p?.id === product.idERP || p?.sku === product.sku
        );
        const now = new Date().toISOString();

        const existingProduct = state.products.find(
          (p) => p?.id === product.id || p?.id === product.idERP || p?.sku === product.sku
        );

        const productUpdated = {
          id: existingProduct?.id || product.id,
          sku: existingProduct?.sku || product.sku,
          name: product.name,
          price: product.price,
          images: Array.isArray(product.images)
            ? [...product.images.map((img: any) => img.url ?? img)]
            : [],
          category: product.category,
          quantity: product.quantity,
          available: product.available,
          coverUrl: product.images?.[0]?.url ?? product.images?.[0],
          colors: Array.isArray(product.colors) ? [...product.colors] : [],
          sizes: Array.isArray(product.sizes) ? [...product.sizes] : [],
          salesLastWeek: product.salesLastWeek,
          salesLastMonth: product.salesLastMonth,
          averagePerWeek: product.averagePerWeek,
          lastUpdatedDate: now,
        };

        if (existingIndex === -1) {
          state.products.push({
            ...productUpdated,
            importedAt: now,
          });
        } else {
          state.products[existingIndex] = {
            ...existingProduct,
            ...productUpdated,
            importedAt: existingProduct.importedAt,
          };
        }
      });
  },
});

// Reducer
export default slice.reducer;

// Actions
export const { resetProducts, updateProducts } = slice.actions;
